import React, { useContext } from "react";
import { useQuery } from "@apollo/client";
import { QUERY_SESSION_DISPLAY_NAME_MAP } from "../../../../../utils/graphql/queries/TPMSQueries";
import { IdsContext } from "../../IdSwitch/IdsContext";
import styles from "./style.module.scss";

const tyrePositions = ["FL", "FR", "RL", "RR"] as const;
type TyrePosition = (typeof tyrePositions)[number];

function getStintStatusKey(stint: Stint): string {
  const dt = stint.tpmsAtStart?.dt ?? "";
  const sesId = stint.sesId ?? "";
  const openLap = stint.openLapNumber ?? "";
  const closeLap = stint.closeLapNumber ?? "";
  return `${sesId}|${dt}|${openLap}|${closeLap}`;
}

function getStintSortTime(stint: Stint): number {
  const dtTime = stint.tpmsAtStart?.dt
    ? new Date(stint.tpmsAtStart.dt).getTime()
    : Number.NaN;
  if (!Number.isNaN(dtTime)) return dtTime;
  if (typeof stint.startTime === "number") return stint.startTime;
  if (typeof stint.finishTime === "number") return stint.finishTime;
  return 0;
}

type Run = {
  session: string;
  title: string;
  ids: Record<TyrePosition, string>;
  lapsInStint: number | null;
  setLaps?: number | null;
  newTyres?: TyrePosition[];
  hasSide?: boolean;
  sidedTyres?: TyrePosition[];
  hasTyreChange?: boolean;
  changedTyres?: TyrePosition[];
};

type SessionGroup = {
  title: string;
  runs: Run[];
};

type Stint = {
  type: string;
  openLapNumber?: number;
  closeLapNumber?: number;
  lapsInStint?: number;
  startTime?: number;
  finishTime?: number;
  sesId?: string;
  sessionName?: string;
  tpmsAtStart?: {
    dt: string;
    frontLeft: number;
    frontRight: number;
    rearLeft: number;
    rearRight: number;
  } | null;
};

export type ModalSessionsListProps = {
  carNum: string;
  sessionsList?: { _id: string; session: string }[];
  stints?: Stint[]; // filtered stints (selected sessions)
  allEventStints?: Stint[]; // all stints for the car in the event
};

const ModalSessionsList: React.FC<ModalSessionsListProps> = ({
  carNum,
  sessionsList = [],
  stints = [],
  allEventStints = [],
}) => {
  const idsContext = useContext(IdsContext);
  if (!idsContext) {
    throw new Error("IdsContext must be used inside an IdsProvider");
  }
  const { showCodes, getCodeForDriverAndId } = idsContext;

  const sessionIds = React.useMemo(
    () =>
      Array.from(new Set(stints.map((stint) => stint.sesId).filter(Boolean))),
    [stints],
  );

  const { data: sessionNameMapData } = useQuery(
    QUERY_SESSION_DISPLAY_NAME_MAP,
    {
      variables: { sessionIds },
      skip: sessionIds.length === 0,
    },
  );

  const sesIdToNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (sessionNameMapData?.getSessionDisplayNameMap) {
      sessionNameMapData.getSessionDisplayNameMap.forEach(
        ({
          aksSessionId,
          displayName,
        }: {
          aksSessionId: string;
          displayName: string;
        }) => {
          map.set(aksSessionId, displayName);
        },
      );
    }
    if (map.size === 0 && sessionsList.length > 0) {
      sessionsList.forEach(({ _id, session }) => {
        map.set(_id, session);
      });
    }
    return map;
  }, [sessionNameMapData, sessionsList]);

  const eventStintStatus = React.useMemo(() => {
    const statusMap = new Map<
      string,
      {
        newTyres: TyrePosition[];
        hasSide: boolean;
        sidedTyres: TyrePosition[];
        hasTyreChange: boolean;
        changedTyres: TyrePosition[];
        setLaps: number | null;
      }
    >();
    const seenTyreIds = new Set<string>();
    const setLapsTracker = new Map<string, number>();
    let previousRun: Run | null = null;
    const sortedAllEventStints = [...allEventStints].sort(
      (a, b) => getStintSortTime(a) - getStintSortTime(b),
    );
    for (const stint of sortedAllEventStints) {
      const tpms = stint.tpmsAtStart;
      const ids: Record<TyrePosition, string> = {
        FL: tpms?.frontLeft?.toString() ?? "-",
        FR: tpms?.frontRight?.toString() ?? "-",
        RL: tpms?.rearLeft?.toString() ?? "-",
        RR: tpms?.rearRight?.toString() ?? "-",
      };
      const curr = ids;
      const prev = previousRun?.ids ?? null;
      // New tyres
      const newTyres = tyrePositions.filter((pos) => {
        const tyreId = curr[pos];
        return tyreId !== "-" && !seenTyreIds.has(tyreId);
      });
      tyrePositions.forEach((pos) => {
        const tyreId = curr[pos];
        if (tyreId !== "-") {
          seenTyreIds.add(tyreId);
        }
      });
      // Sided/changed logic
      let sideDetected = false;
      const sidedTyres: TyrePosition[] = [];
      const leftPositions: TyrePosition[] = ["FL", "RL"];
      const rightPositions: TyrePosition[] = ["FR", "RR"];
      if (prev) {
        tyrePositions.forEach((pos) => {
          const prevTyre = prev[pos];
          if (prevTyre === "-" || curr[pos] === "-") return;
          const prevPos = tyrePositions.find((p) => prev[p] === curr[pos]);
          if (
            prevPos &&
            ((leftPositions.includes(pos) &&
              rightPositions.includes(prevPos)) ||
              (rightPositions.includes(pos) && leftPositions.includes(prevPos)))
          ) {
            sideDetected = true;
            if (!sidedTyres.includes(pos)) sidedTyres.push(pos);
          }
        });
      }
      const changedTyres = prev
        ? tyrePositions.filter((pos) => {
            const currentTyre = curr[pos];
            const previousTyre = prev[pos];
            const isDifferent = currentTyre !== previousTyre;
            const isNotNew = !newTyres.includes(pos);
            const isNotSided = !sidedTyres.includes(pos);
            const isValidTyre = currentTyre !== "-" && previousTyre !== "-";
            return isDifferent && isNotNew && isNotSided && isValidTyre;
          })
        : [];
      // Set laps
      const currentTyreSet = tyrePositions.map((pos) => curr[pos]);
      const hasCompleteSet = currentTyreSet.every((tyre) => tyre !== "-");
      let setLaps: number | null = null;
      if (hasCompleteSet) {
        const sortedSetKey = [...currentTyreSet].sort().join("-");
        setLaps = setLapsTracker.get(sortedSetKey) ?? 0;
        const currentLaps = stint.lapsInStint ?? 0;
        setLapsTracker.set(
          sortedSetKey,
          (setLapsTracker.get(sortedSetKey) ?? 0) + currentLaps,
        );
      }
      statusMap.set(getStintStatusKey(stint), {
        newTyres,
        hasSide: sideDetected,
        sidedTyres,
        hasTyreChange: changedTyres.length > 0,
        changedTyres,
        setLaps,
      });
      previousRun = { ids: curr } as Run;
    }
    return statusMap;
  }, [allEventStints]);

  // Group selected stints for display
  const grouped = new Map<string, Stint[]>();
  for (const stint of stints) {
    const sesId = stint.sesId || "unknown";
    if (!grouped.has(sesId)) grouped.set(sesId, []);
    grouped.get(sesId)?.push(stint);
  }

  const sessionGroups: SessionGroup[] = [];
  let sessionIndex = 1;
  for (const [sesId, sessionStints] of grouped.entries()) {
    const sessionName =
      sesIdToNameMap.get(sesId) ||
      sessionStints[0]?.sessionName ||
      `Session ${sessionIndex}`;

    const runs: Run[] = sessionStints.map((stint, index) => {
      const tpms = stint.tpmsAtStart;
      const ids: Record<TyrePosition, string> = {
        FL: showCodes
          ? getCodeForDriverAndId(carNum, tpms?.frontLeft?.toString())
          : tpms?.frontLeft?.toString() ?? "-",
        FR: showCodes
          ? getCodeForDriverAndId(carNum, tpms?.frontRight?.toString())
          : tpms?.frontRight?.toString() ?? "-",
        RL: showCodes
          ? getCodeForDriverAndId(carNum, tpms?.rearLeft?.toString())
          : tpms?.rearLeft?.toString() ?? "-",
        RR: showCodes
          ? getCodeForDriverAndId(carNum, tpms?.rearRight?.toString())
          : tpms?.rearRight?.toString() ?? "-",
      };
      // Use event-wide status for this stint
      const statusKey = getStintStatusKey(stint);
      const status = eventStintStatus.get(statusKey) ?? {
        newTyres: [],
        hasSide: false,
        sidedTyres: [],
        hasTyreChange: false,
        changedTyres: [],
        setLaps: null,
      };
      return {
        session: sessionName,
        title: `RUN ${index + 1}`,
        ids,
        lapsInStint: stint.lapsInStint ?? null,
        newTyres: status.newTyres,
        hasSide: status.hasSide,
        sidedTyres: status.sidedTyres,
        hasTyreChange: status.hasTyreChange,
        changedTyres: status.changedTyres,
        setLaps: status.setLaps,
      };
    });
    sessionGroups.push({
      title: sessionName,
      runs,
    });
    sessionIndex++;
  }

  return (
    <div className={styles.wrapper} key={carNum}>
      {sessionGroups.map((group: SessionGroup, idx: number) => (
        <div key={idx} className={styles.sessionGroup}>
          <div className={styles.groupTitle}>{group.title}</div>
          <div className={styles.runList}>
            {group.runs.map((run: Run, runIdx: number) => (
              <div
                key={runIdx}
                className={`${styles.runItem} ${
                  run.newTyres?.length
                    ? styles.newTyre
                    : run.hasSide
                    ? styles.sided
                    : run.hasTyreChange
                    ? styles.tyreChange
                    : ""
                }`}
                style={{ position: "relative" }}
              >
                <div className={styles.splitRow}>
                  <div className={styles.leftBox}>
                    <div className={styles.runHeader}>
                      <div className={styles.runTitle}>{run.title}</div>
                      {run.setLaps !== null && run.setLaps !== undefined && (
                        <div
                          className={styles.setLapsBox}
                          style={{
                            fontSize: "0.85em",
                            fontWeight: "normal",
                            textAlign: "center",
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            color: "#CCCCCC",
                          }}
                        >
                          Set = {run.setLaps} Laps
                        </div>
                      )}
                      <div className={styles.tagGroup}>
                        {(run.newTyres?.length ?? 0) > 0 && (
                          <span className={styles.newTyreTag}>New Tyre</span>
                        )}
                        {run.hasTyreChange && (
                          <span className={styles.tyreChangeTag}>
                            Tyre Change
                          </span>
                        )}
                        {run.hasSide && (
                          <span className={styles.sideTag}>Sided</span>
                        )}
                      </div>
                    </div>

                    <div className={styles.tyreGrid}>
                      {tyrePositions.map((pos) => (
                        <div
                          key={pos}
                          className={`${styles.cell} ${
                            run.sidedTyres?.includes(pos)
                              ? styles.tyreSided
                              : run.newTyres?.includes(pos)
                              ? styles.tyreNew
                              : run.changedTyres?.includes(pos)
                              ? styles.tyreChanged
                              : ""
                          }`}
                        >
                          {run.ids[pos]}
                        </div>
                      ))}
                    </div>

                    <div
                      className={styles.lapsCountBox}
                      style={{ fontWeight: "bold" }}
                    >
                      Stint = {run.lapsInStint ?? "-"} Laps
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ModalSessionsList;
